import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';

import { cn } from '../../lib/utils';
import { listTimeZones, zoneLabel, type ZoneOption } from '../../lib/timeZones';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const CAMPAIGN_ZONE_LABEL = 'Campaign zone';
const PLACEHOLDER = 'Select a time zone';

export interface TimeZoneSelectProps {
  /** `null` means "inherit" — the campaign zone — when `allowCampaignZone`. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** Adds the leading "Campaign zone" option, for per-entry override pickers. */
  allowCampaignZone?: boolean;
  /** e.g. 'Campaign zone (India · Asia/Kolkata)'. */
  campaignZoneLabel?: string;
  /** The accessible label, e.g. "Campaign time zone". */
  label: string;
  disabled?: boolean;
  /** Focus the trigger on mount, for a picker revealed by a click elsewhere. */
  autoFocus?: boolean;
  /** Id of text describing the picker, e.g. a note on what a change does. */
  describedBy?: string;
  /** Marks the trigger invalid, so a refusal raised against the zone can focus it. */
  invalid?: boolean;
}

interface ZoneGroup {
  region: string;
  zones: ZoneOption[];
  /** Flat index of this group's first zone, for keyboard navigation. */
  start: number;
}

function groupByRegion(zones: ZoneOption[], offset: number): ZoneGroup[] {
  const groups: ZoneGroup[] = [];
  zones.forEach((zone, index) => {
    const current = groups[groups.length - 1];
    if (current && current.region === zone.region) {
      current.zones.push(zone);
      return;
    }
    groups.push({ region: zone.region, zones: [zone], start: offset + index });
  });
  return groups;
}

/**
 * Searchable IANA zone picker: a Popover holding a filter input over a
 * region-grouped listbox. `@discovr/ui` has no Combobox primitive and this
 * needs no new dependency, so keyboard handling lives on the filter input —
 * typing filters, ArrowUp/Down move the active option, Enter picks it, Escape
 * closes (Radix's dismissable layer).
 */
export function TimeZoneSelect({
  value,
  onChange,
  allowCampaignZone = false,
  campaignZoneLabel,
  label,
  disabled,
  autoFocus,
  describedBy,
  invalid,
}: TimeZoneSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const baseId = useId();

  const zones = useMemo(() => listTimeZones(), []);
  const campaignLabel = campaignZoneLabel ?? CAMPAIGN_ZONE_LABEL;
  const term = query.trim().toLowerCase();

  const matches = useMemo(
    () => (term ? zones.filter((zone) => zone.search.includes(term)) : zones),
    [zones, term],
  );
  const showCampaign = allowCampaignZone && (!term || campaignLabel.toLowerCase().includes(term));
  const offset = showCampaign ? 1 : 0;
  const groups = useMemo(() => groupByRegion(matches, offset), [matches, offset]);
  const rowCount = offset + matches.length;

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [open, activeIndex]);

  const select = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  const selectActive = () => {
    if (showCampaign && activeIndex === 0) {
      select(null);
      return;
    }
    const zone = matches[activeIndex - offset];
    if (zone) select(zone.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(Math.min(index + 1, rowCount - 1), 0));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectActive();
    }
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    // A new filter re-numbers the rows, so the active row goes back to the top.
    setActiveIndex(0);
  };

  /** Open on the current value — the unfiltered list is hundreds of rows long. */
  const selectedIndex = () => {
    if (value === null) return 0;
    const index = zones.findIndex((zone) => zone.id === value);
    return index < 0 ? 0 : index + (allowCampaignZone ? 1 : 0);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setQuery('');
    setActiveIndex(next ? selectedIndex() : 0);
  };

  const triggerText = value
    ? zoneLabel(value)
    : allowCampaignZone
      ? campaignLabel
      : PLACEHOLDER;

  const optionId = (index: number) => `${baseId}-option-${index}`;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={label}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : undefined}
          autoFocus={autoFocus}
          disabled={disabled}
          className="w-full justify-between rounded-md px-3 font-normal"
        >
          <span className="truncate">{triggerText}</span>
          <CaretDown className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="border-border border-b p-2">
          <div className="relative">
            <MagnifyingGlass
              className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              ref={inputRef}
              value={query}
              aria-label="Search zones"
              aria-controls={`${baseId}-listbox`}
              aria-activedescendant={rowCount > 0 ? optionId(activeIndex) : undefined}
              placeholder="Search zones…"
              className="pl-8"
              onChange={(event) => handleQueryChange(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <ul
          id={`${baseId}-listbox`}
          ref={listRef}
          role="listbox"
          aria-label={label}
          className="max-h-64 overflow-auto py-1"
        >
          {showCampaign && (
            <li
              id={optionId(0)}
              role="option"
              aria-selected={value === null}
              data-index={0}
              className={cn(
                'cursor-pointer px-3 py-2 text-sm',
                activeIndex === 0 && 'bg-accent text-accent-foreground',
              )}
              onClick={() => select(null)}
              onMouseEnter={() => setActiveIndex(0)}
            >
              {campaignLabel}
            </li>
          )}
          {groups.map((group) => (
            <li key={group.region} role="group" aria-label={group.region}>
              <div className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-medium">
                {group.region}
              </div>
              <ul role="none">
                {group.zones.map((zone, index) => {
                  const flatIndex = group.start + index;
                  return (
                    <li
                      key={zone.id}
                      id={optionId(flatIndex)}
                      role="option"
                      aria-selected={zone.id === value}
                      data-index={flatIndex}
                      className={cn(
                        'cursor-pointer px-3 py-2 text-sm',
                        flatIndex === activeIndex && 'bg-accent text-accent-foreground',
                      )}
                      onClick={() => select(zone.id)}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                    >
                      {zone.label}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>

        {rowCount === 0 && (
          <div className="text-muted-foreground px-3 py-2 text-sm">No zones match your search.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
